import * as React from 'react'
import { ThemeProvider, DefaultTheme } from 'styled-components'
import { offeredServiceResourcesList, RansackQuery } from '../../../services/models/offered-service-resources.service'
import { MultiOpeningHours, OfferedServiceType, WorkingDay } from '../../../services/models/offered-services.service'
import { errorsStateHandler } from '../../../services/utils/errors.service'
import { I18nService } from '../../../services/utils/i18n.service'
import { TypedID } from '../../../services/utils/iots.service'
import { injectTheme } from '../../../theme'
import ErrorBoxComponent from '../../shared/error-box.component'
import LoadingComponent from '../../shared/loading.component'
import SelectableListComponent from '../../shared/selectable-list.component'
import OfferedServiceResourcesFilterComponent from './offered-service-resources-filter.component'

interface Props {
  query?: RansackQuery
  offeredServices: {
    id: TypedID<'OfferedService'>
    name: string
    offered_service_type: OfferedServiceType
  }[]
  availableForBody?: RansackQuery['available_for']
  serviceDeliveryLocations?: { id: TypedID<'ServiceDeliveryLocation'>, name: string, inline_address: string }[]
  onResult: (_: State['offeredServiceResources'][0]) => void
  onCancel: () => void
}

interface State {
  query: RansackQuery
  offeredServiceResources: {
    id: TypedID<'OfferedServiceResource'>
    ref: string
    working_days: WorkingDay[]
    multi_opening_hours: MultiOpeningHours,
    offered_service_stock: {
      offered_service_combination: {
        name: string
      }
    }
  }[]
  loading: boolean
  hasMore: boolean
  errors: string[]
}

const i18nScoped = new I18nService(require('./offered-service-resources-picker-modal.component.yml'))

@injectTheme
export default class OfferedServiceResourcesPickerModalComponent extends React.Component<Props & { theme?: DefaultTheme }, State> {
  state: State = { query: this.props.query || {}, offeredServiceResources: [], hasMore: false, loading: false, errors: [] }

  async componentDidMount () {
    await this.execQuery()
  }

  @errorsStateHandler(function (this: OfferedServiceResourcesPickerModalComponent, errors) { this.setState({ errors }) })
  private async execQuery (loadMore = false) {
    this.setState({ loading: true })

    const query = { ...this.state.query }
    if (loadMore) query['q[id_not_in][]'] = this.state.offeredServiceResources.map(_ => _.id)
    const result = await offeredServiceResourcesList(query)
    if (loadMore) result.offered_service_resources.unshift(...this.state.offeredServiceResources as typeof result.offered_service_resources)
    result.offered_service_resources.sort((a, b) => a.ref.localeCompare(b.ref))
    this.setState({ offeredServiceResources: result.offered_service_resources, hasMore: result.has_more, loading: false })
    return true
  }

  render () {
    const { errors, query, offeredServiceResources, loading, hasMore } = this.state

    return <div style={{ padding: '1em' }}>
      <ErrorBoxComponent fullMessages={ errors } />

      <div className="grid">
        <div className="grid-1-3 u-borderR u-prm">
          <OfferedServiceResourcesFilterComponent
            query={ query }
            availableForBody={ this.props.availableForBody }
            offeredServices={ this.props.offeredServices }
            serviceDeliveryLocations={ this.props.serviceDeliveryLocations }
            onChange={ query => this.setState({ query }) }
            onSubmit={ () => this.execQuery() } />
        </div>

        <div className="grid-2-3">
          <label className="form-label">{ i18nScoped.t('select_one_in_list') }</label>

          <div style={{ maxHeight: '23em', overflowY: 'auto' }}>
            <ThemeProvider theme={{ ...this.props.theme, selectableListOnComputerWithOuterBorder: false }}>
              <SelectableListComponent
                name="offeredServiceResource"
                data={ offeredServiceResources }
                value={ null }
                formatter={ resource => <>
                  <div style={{ fontWeight: 'bold' }}>{ resource.ref }</div>
                  { resource.offered_service_stock.offered_service_combination.name }
                </> }
                onChange={ this.props.onResult } />
            </ThemeProvider>

            { loading && <LoadingComponent presentation='inline' align='center' /> }

            { !loading && !offeredServiceResources.length && <div className="message">
              <span className="icon icon-info"></span>
              <p>{ i18nScoped.t('no_content') }</p>
            </div> }

            { hasMore && <p className="u-txtCenter">
              <a onClick={ () => this.execQuery(true) }>{ i18nScoped.t('see_more') }</a>
            </p> }
          </div>
        </div>
      </div>
    </div>
  }
}
